import axios from "axios";

const photoCache = new Map();

async function fetchGraphPhotoObjectUrl(photoUrl, authService) {
  if (!authService || typeof authService.getAccessToken !== "function") {
    return null;
  }

  const token = await authService.getAccessToken({ scopes: ["User.Read"] });
  if (!token || typeof token.accessToken !== "string" || token.accessToken.trim() === "") {
    return null;
  }

  const response = await axios.get(photoUrl, {
    responseType: "arraybuffer",
    headers: {
      Authorization: `Bearer ${token.accessToken}`,
    },
  });

  const contentType = response?.headers?.["content-type"] || "image/jpeg";
  const photoBlob = new Blob([response.data], { type: contentType });
  if (photoBlob.size === 0) {
    return null;
  }

  return URL.createObjectURL(photoBlob);
}

export async function getGraphPhotoObjectUrl(photoUrl, authService) {
  const normalizedPhotoUrl = (photoUrl || "").trim();
  if (normalizedPhotoUrl === "") {
    return null;
  }

  const cached = photoCache.get(normalizedPhotoUrl);
  if (cached) {
    if (cached.status === "resolved") {
      return cached.objectUrl;
    }
    if (cached.status === "not_found") {
      return null;
    }
    if (cached.status === "pending") {
      return cached.promise;
    }
  }

  const loadPromise = (async () => {
    try {
      const objectUrl = await fetchGraphPhotoObjectUrl(normalizedPhotoUrl, authService);
      if (!objectUrl) {
        photoCache.set(normalizedPhotoUrl, { status: "not_found" });
        return null;
      }
      photoCache.set(normalizedPhotoUrl, {
        status: "resolved",
        objectUrl,
      });
      return objectUrl;
    } catch (error) {
      if (error?.response?.status === 404) {
        photoCache.set(normalizedPhotoUrl, { status: "not_found" });
        return null;
      }
      photoCache.delete(normalizedPhotoUrl);
      throw error;
    }
  })();

  photoCache.set(normalizedPhotoUrl, {
    status: "pending",
    promise: loadPromise,
  });

  return loadPromise;
}

export function clearGraphPhotoCache() {
  photoCache.forEach((entry) => {
    if (entry?.status === "resolved" && entry.objectUrl) {
      URL.revokeObjectURL(entry.objectUrl);
    }
  });
  photoCache.clear();
}
